<script lang="ts" setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import useFeatureFlags from '@/app/core/composable/feature-flags/useFeatureFlags'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { deleteProcessRequest } from '@/app/core/services/pending-processes'
import { usePayrollStore } from '@/store/payroll-data'
import PayrollSheet from '../components/PayrollSheet.vue'
import {
  DCard,
  Users01Icon,
  DLoading,
  Loading02Icon,
  DModal,
  DDropdown,
  DBtn,
  CheckVerified01Icon,
  CheckIcon,
  AlertCircleIcon,
  Download01Icon,
  RefreshCw01Icon,
  FileCheck02Icon
} from 'front-library'
import {
  getDataProcessedPayroll,
  closePayroll,
  markPayrollAsPaid,
  generateBankFiles
} from '../../../core/services/processed-payrolls'
import {
  ReportCustomizedPayroll,
  getReportRequest,
  generateCustomizedPayroll
} from '../../../core/services/reports'
import {
  BankFile,
  listBankFiles,
  getBankFileDownload,
  getBankTemplateEntities
} from '../../../core/services/bank-files'
usePayrollChange(() => router.go(-1))

/* store, router, notifications */
const route = useRoute()
const router = useRouter()
const store = useUserStore()
const payrollStore = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()
const { isEnabled } = useFeatureFlags()

/* Datos */
const payloadHistoric = ref<any>({})
const dataSheet = ref<any>({})
const loading = ref(false)
const menuOptions = ref<any>([])
const id = ref<any>(route.params.id)
const type = ref<any>(route.params.type)
const idPendingProcesses = ref<any>(route.params?.idPendingProcesses)

/* Datos componente Modal */
const showModal = ref(false)
const showModalExport = ref(false)
const showModalDelete = ref(false)
const showModalPaid = ref(false)
const showModalBankFiles = ref(false)
const titleModal = ref()
const descriptionModal = ref()
const acceptQuestion = ref()
const isClose = ref(false)
const typeAlertModal = ref('')
const isPaid = ref(false)

/* Feature flag: habilita marcar la nomina como pagada.
   Se evalua por compania activa via Flagsmith (reactivo). */
const isPaidFeatureEnabled = computed(() => isEnabled('payroll_paid'))

/* Feature flag: entregables (archivos bancarios y otros documentos generados).
   Habilita la regeneracion y el listado. Por ahora solo en dev. */
const isBankFilesFeatureEnabled = computed(() => isEnabled('new_bank_files'))

const message = ref<string>(t('loading.data'))
const urlFile = ref(null)
const refresh = ref()

/* Vista activa del detalle: sheet de nomina o entregables generados */
type SheetView = 'sheet' | 'bankFiles'
const sheetView = ref<SheetView>('sheet')
const bankFiles = ref<BankFile[]>([])
/* templateId -> entidad destinataria (banco, ente de gobierno, etc.) */
const bankFileEntities = ref<Map<string, string>>(new Map())
const loadingBankFiles = ref(false)
const downloadingBankFileId = ref('')

const canShowBankFiles = computed(
  () =>
    isBankFilesFeatureEnabled.value &&
    dataSheet.value.status !== 'PRENOMINA' &&
    Boolean(dataSheet.value.closedPayrollId)
)

/* Regenerar entregables: mismas condiciones del listado + permiso de cierre */
const canRegenerateBankFiles = computed(
  () => canShowBankFiles.value && store.hasPermission('payroll_closed:create')
)

watch(isBankFilesFeatureEnabled, enabled => {
  if (!enabled && sheetView.value === 'bankFiles') sheetView.value = 'sheet'
})

const selectSheetView = async (view: SheetView) => {
  if (sheetView.value === view) return
  sheetView.value = view
  if (view === 'bankFiles') await loadBankFiles()
}

const loadBankFiles = async () => {
  if (!dataSheet.value.closedPayrollId) return
  loadingBankFiles.value = true
  try {
    const [files, entities] = await Promise.all([
      listBankFiles(dataSheet.value.closedPayrollId),
      getBankTemplateEntities()
    ])
    bankFiles.value = files
    bankFileEntities.value = entities
  } catch (error: any) {
    notificationError(error, '', t, notifications, loadingBankFiles)
  } finally {
    loadingBankFiles.value = false
  }
}

const bankFileEntity = (templateId: string) =>
  bankFileEntities.value.get(templateId) ?? templateId

const bankFileName = (file: BankFile) =>
  file.s3Key ? file.s3Key.slice(file.s3Key.lastIndexOf('/') + 1) : ''

const formatBankFileAmount = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

const downloadBankFile = async (file: BankFile) => {
  downloadingBankFileId.value = file.id
  try {
    const download = await getBankFileDownload(file.id)
    if (!download.downloadUrl) return
    /* URL prefirmada de S3 apuntando a un archivo: descarga en la misma pestana */
    window.location.href = download.downloadUrl
  } catch (error: any) {
    notificationError(error, '', t, notifications, loadingBankFiles)
  } finally {
    downloadingBankFileId.value = ''
  }
}

/*  */
const runAction = (val: any) => {
  switch (val) {
    case 'closePayroll':
      showModal.value = true
      titleModal.value = t('payrollSheet.confirmClose')
      descriptionModal.value = t('modal.description')
      acceptQuestion.value = t('payrollSheet.yesClose')
      isClose.value = true
      break
    case 'delete':
      showModalDelete.value = true
      titleModal.value = t('payrollSheet.confirmDelete')
      descriptionModal.value = t('modal.description')
      acceptQuestion.value = t('payrollSheet.yesDelete')
      break
    case 'export':
      const formatStartDate: any = dataSheet.value.startDate2
      const formatEndDate: any = dataSheet.value.endDate2
      const createReport: ReportCustomizedPayroll = {
        code: 'basics/customized-payroll',
        parameters: {
          payrolls: [dataSheet.value.payroll_id],
          processes: [dataSheet.value.processCode],
          startDate: formatStartDate,
          endDate: formatEndDate,
          status: dataSheet.value.status,
          concepts: [...dataSheet.value.listconcepts],
          employees: [...dataSheet.value.listemployees],
          showAmount: true,
          showCodes: true,
          showQuantity: true,
          processedPayroll: id.value
        }
      }
      generateReport(createReport)
      break
    default:
      break
  }
}
const setLoading = (val: boolean) => {
  loading.value = val
}

/* Cerrar nomina */
const closedPayroll = async () => {
  showModal.value = false
  loading.value = true
  message.value = t('payrollSheet.close.closingPayroll')
  try {
    const data = {
      processedPayrollId: dataSheet.value.id,
      closedBy: store?.user?.name as string
    }
    await closePayroll(data)
    dataSheet.value = await getDataProcessedPayroll(id.value)
    loading.value = false
    setValuesMenuOptions(true)
    notifications.addNotification({
      title: t('payrollSheet.close.closed'),
      text: t('payrollSheet.close.payrollClosedSuccessfully'),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* eliminar nómina */
const deletePayroll = async () => {
  loading.value = true
  showModalDelete.value = false
  await deleteProcessRequest(idPendingProcesses.value)
    .then((response: any) => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('pendingProcesses.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      setValuesMenuOptions(true)
      void router.push({ name: 'ListProcessRequests' })
    })
    .catch((error: any) => {
      notificationError(error, '', t, notifications, loading)
    })
}

/* Marcar como pagada */
const confirmPaid = () => {
  showModalPaid.value = true
  titleModal.value = t('payrollSheet.paid.confirmTitle')
  descriptionModal.value = t('payrollSheet.paid.confirm')
  acceptQuestion.value = t('payrollSheet.paid.yesPaid')
}

const handleMarkAsPaid = async () => {
  showModalPaid.value = false
  loading.value = true
  try {
    if (!dataSheet.value.closedPayrollId)
      throw new Error(t('notification.error'))
    const ok = await markPayrollAsPaid(dataSheet.value.closedPayrollId)
    if (!ok) throw new Error(t('notification.error'))
    isPaid.value = true
    loading.value = false
    notifications.addNotification({
      title: t('payrollSheet.paid.label'),
      text: t('payrollSheet.paid.success'),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* Regenerar entregables (archivos bancarios y demas documentos generados) */
const confirmGenerateBankFiles = () => {
  showModalBankFiles.value = true
  titleModal.value = t('payrollSheet.bankFiles.confirm')
  descriptionModal.value = t('payrollSheet.bankFiles.description')
  acceptQuestion.value = t('payrollSheet.bankFiles.yesGenerate')
}

const handleGenerateBankFiles = async () => {
  showModalBankFiles.value = false
  loading.value = true
  message.value = t('payrollSheet.bankFiles.requesting')
  try {
    if (!dataSheet.value.closedPayrollId)
      throw new Error(t('notification.error'))
    const ok = await generateBankFiles(dataSheet.value.closedPayrollId)
    if (!ok) throw new Error(t('notification.error'))
    loading.value = false
    notifications.addNotification({
      title: t('payrollSheet.bankFiles.label'),
      text: t('payrollSheet.bankFiles.success'),
      type: 'success',
      time: 10000
    })
    if (sheetView.value === 'bankFiles') await loadBankFiles()
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalExport.value = false
  showModalDelete.value = false
  showModalPaid.value = false
  showModalBankFiles.value = false
}

/* opciones del menu */
const menuWithoutErrors = ref(true)
const setValuesMenuOptions = (error: boolean) => {
  menuWithoutErrors.value = error
  menuOptions.value = [
    {
      name: t('payrollSheet.button.closePayroll'),
      action: 'closePayroll',
      view:
        dataSheet.value.status === 'PRENOMINA' &&
        dataSheet.value.errors.length === 0 &&
        error &&
        store.hasPermission('payroll_closed:create') &&
        dataSheet.value.processCode != 'L'
    },
    {
      name: t('payrollSheet.button.delete'),
      action: 'delete',
      view: dataSheet.value.status === 'PRENOMINA'
    },
    {
      name: t('payrollSheet.button.export'),
      action: 'export',
      view: true
    }
  ]
}

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  message.value = t('payrollSheet.close.generatingReport')
  try {
    const response = await generateCustomizedPayroll(values)
    reportRequest(response)
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* generar reporte */
const reportRequest = async (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('payrollSheet.modal.reportGenerated')
        showModalExport.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('payrollSheet.modal.reportNotGenerated')
        descriptionModal.value = response.errors
        showModalExport.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error) {
      notificationError(error, '', t, notifications, loading)
    }
  }, 5000)
}

onMounted(async () => {
  loading.value = true
  try {
    dataSheet.value = await getDataProcessedPayroll(id.value)
    if (dataSheet.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('pendingProcesses.notification.error'),
        type: 'error',
        time: 10000
      })
      void router.push({ name: 'ListProcessRequests' })
    }
    if (dataSheet.value.payrollCode !== payrollStore.payroll?.code) {
      payrollStore.changeSelectedPayrollByCode(dataSheet.value.payrollCode)
    }
    payloadHistoric.value = {
      type: type.value,
      companyCode: dataSheet.value.companyCode,
      payrollCode: dataSheet.value.payrollCode,
      alterCodesConcepts: dataSheet.value.alterCodesConcepts,
      alterNamesConcepts: dataSheet.value.alterNamesConcepts,
      process: dataSheet.value.processCode,
      endDate: dataSheet.value.endDate.split('-').reverse().join('-'),
      processedPayrollId: id.value,
      processRequestId: dataSheet.value.processRequestId || id.value
    }
    isPaid.value = !!dataSheet.value.isPaid
    loading.value = false
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="message" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="closedPayroll()" />
  <DModal
    :open="showModalExport"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('payrollSheet.viewFile')" />
    </template>
  </DModal>
  <DModal
    :open="showModalDelete"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deletePayroll()" />
  <DModal
    :open="showModalPaid"
    typeAlert="warning"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal">
    <template v-slot:actions>
      <footer
        class="sticky inset-x-0 bottom-0 bg-gray-100 w-full flex items-center justify-center pt-5 pb-5 h-17">
        <DBtn
          type="button"
          color="secondary"
          class="py-2 px-8 mx-2"
          @click="closeModal">
          {{ t('payrollSheet.paid.cancel') }}
        </DBtn>
        <DBtn
          type="button"
          color="warning"
          class="py-2 px-8 mx-2"
          @click="handleMarkAsPaid()">
          {{ acceptQuestion }}
        </DBtn>
      </footer>
    </template>
  </DModal>
  <DModal
    :open="showModalBankFiles"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="handleGenerateBankFiles()" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-8">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('payrollSheet.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto pr-5" style="height: calc(100% - 5rem)">
      <DCard>
        <div class="grid grid-cols-10">
          <div
            class="col-span-4 xl:col-span-4 2xl:col-span-3 flex flex-col gap-1">
            <div class="flex items-center justify-between pr-3">
              <h1 class="text-lg">{{ dataSheet.payrollName }}</h1>
              <span
                class="text-green-600 bg-green-100 px-3 rounded-xl font-bold py-1">
                {{ dataSheet.statusName }}
              </span>
              <template
                v-if="
                  dataSheet.status !== 'PRENOMINA' &&
                  isPaidFeatureEnabled &&
                  dataSheet.closedPayrollId &&
                  store.hasPermission('payroll_closed:create')
                ">
                <!-- Estado final: nómina pagada (no interactivo) -->
                <span
                  v-if="isPaid"
                  class="flex items-center gap-1 text-green-600 bg-green-100 px-3 rounded-xl font-bold py-1">
                  <CheckVerified01Icon class="h-4 w-4" />
                  {{ t('payrollSheet.paid.label') }}
                </span>
                <!-- Acción irreversible: marcar como pagada -->
                <DBtn
                  v-else
                  type="button"
                  color="success"
                  class="whitespace-nowrap"
                  @click="confirmPaid">
                  {{ t('payrollSheet.paid.markAsPaid') }}
                </DBtn>
              </template>
            </div>
            <div class="flex gap-4">
              <p class="font-medium">
                {{ t('payrollSheet.list.type') }}
                <span
                  class="text-green-600 bg-green-100 px-3 rounded-xl font-bold py-1">
                  {{ dataSheet.type }}
                </span>
              </p>
              <div class="flex gap-4">
                <Users01Icon />
                <p class="font-semibold">{{ dataSheet.employees }}</p>
              </div>
            </div>
            <div class="flex gap-4">
              <p class="font-medium">
                {{ t('payrollSheet.list.period') }}
                <span class="font-bold">
                  {{ dataSheet.startDate }} - {{ dataSheet.endDate }}
                </span>
              </p>
            </div>
          </div>
          <div
            class="flex flex-col xl:col-span-5 2xl:col-span-6 border-0 border-t-1 lg:border-l-4 px-2 lg:border-t-0">
            <h3>{{ t('payrollSheet.list.parametersUsed') }}</h3>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="(param, index) in dataSheet.parameters"
                :key="index"
                class="overflow-auto py-1 px-2 rounded-xl bg-gray-100 text-gray-500 text-sm font-semibold">
                {{ index }}:{{ param }}
              </label>
            </div>
          </div>
          <div class="flex justify-end col-span-1">
            <DDropdown
              :options="menuOptions"
              @action="(val: any) => runAction(val)" />
          </div>
        </div>
      </DCard>
      <DCard
        v-if="dataSheet?.errors?.length > 0"
        style="
          background-color: rgba(252, 173, 173, 0.288);
          border: 3px solid rgba(255, 165, 165, 0.288);
          margin-top: 24px;
          margin-bottom: 24px;
          max-height: 200px;
          overflow-y: auto;
        ">
        <h1 class="text-red-700" v-html="t('payrollSheet.list.mistakes')" />
        <ul
          class="list-inside ..."
          style="list-style-type: disc"
          v-for="(error, index) in dataSheet.errors"
          :key="index">
          <li
            class="text-red-500 whitespace-break-spaces text-justify text-sm font-semibold">
            {{ error }}
          </li>
        </ul>
      </DCard>
      <!-- Switch de vista (mismos estilos de pestana que DTabs) -->
      <ul
        v-if="canShowBankFiles"
        class="flex flex-row overflow-auto scroll mt-4">
        <li
          class="text-base font-semibold tracking-normal p-3 text-center cursor-pointer whitespace-nowrap"
          :class="
            sheetView === 'sheet'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-400 border-b-2 border-gray-200'
          "
          @click="selectSheetView('sheet')">
          {{ t('payrollSheet.bankFiles.tabs.sheet') }}
        </li>
        <li
          class="text-base font-semibold tracking-normal p-3 text-center cursor-pointer whitespace-nowrap"
          :class="
            sheetView === 'bankFiles'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-400 border-b-2 border-gray-200'
          "
          @click="selectSheetView('bankFiles')">
          {{ t('payrollSheet.bankFiles.tabs.files') }}
          <span v-if="bankFiles.length">({{ bankFiles.length }})</span>
        </li>
        <li class="flex-1 border-b-2 border-gray-200" />
      </ul>
      

      <Suspense v-if="payloadHistoric.type && sheetView === 'sheet'">
        <PayrollSheet
          :dataHistoric="payloadHistoric"
          :employees="dataSheet.employees"
          @loading="setLoading"
          @isError="setValuesMenuOptions" />
        <template #fallback>
          <div
            class="h-14 border rounded-md bg-gray-200 flex gap-2 items-center px-3 text-gray-500 mt-3">
            <Loading02Icon class="animate-spin text-gray-500" />
            <p>{{ t('payrollSheet.close.loadingPayrollSheet') }}</p>
          </div>
        </template>
      </Suspense>

      <!-- Vista: entregables generados (archivos bancarios y otros documentos) -->
      <DCard v-if="sheetView === 'bankFiles'" class="mt-4">
        <div class="flex items-center justify-between gap-4 px-3 pt-2">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              {{ t('payrollSheet.bankFiles.label') }}
            </h3>
            <p class="text-sm text-gray-500">
              {{ t('payrollSheet.bankFiles.listDescription') }}
            </p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <DBtn
              type="button"
              color="secondary"
              :disabled="loadingBankFiles"
              @click="loadBankFiles()">
              <RefreshCw01Icon
                class="w-4 h-4 mr-2"
                :class="{ rotate: loadingBankFiles }" />
              {{ t('payrollSheet.bankFiles.refresh') }}
            </DBtn>
            <DBtn
              v-if="canRegenerateBankFiles"
              type="button"
              :disabled="loadingBankFiles"
              @click="confirmGenerateBankFiles()">
              <FileCheck02Icon class="w-4 h-4 mr-2" />
              {{ t('payrollSheet.bankFiles.regenerate') }}
            </DBtn>
          </div>
        </div>
        <div class="overflow-x-auto scroll px-3 pb-3">
          <table class="w-full text-sm mt-4">
            <thead>
              <tr class="text-left text-gray-500">
                <th class="py-2 pr-3 font-medium">
                  {{ t('payrollSheet.bankFiles.columns.entity') }}
                </th>
                <th class="py-2 px-3 font-medium">
                  {{ t('payrollSheet.bankFiles.columns.file') }}
                </th>
                <th class="py-2 px-3 font-medium">
                  {{ t('payrollSheet.bankFiles.columns.status') }}
                </th>
                <th class="py-2 px-3 font-medium text-right">
                  {{ t('payrollSheet.bankFiles.columns.transactions') }}
                </th>
                <th class="py-2 px-3 font-medium text-right">
                  {{ t('payrollSheet.bankFiles.columns.amount') }}
                </th>
                <th class="py-2 pl-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingBankFiles">
                <td colspan="6" class="py-8 text-center text-gray-400">
                  <span class="inline-flex items-center gap-2">
                    <Loading02Icon class="w-5 h-5 text-blue-500 rotate" />
                    {{ t('payrollSheet.bankFiles.loading') }}
                  </span>
                </td>
              </tr>
              <tr v-else-if="bankFiles.length === 0">
                <td colspan="6" class="py-8 text-center text-gray-400">
                  {{ t('payrollSheet.bankFiles.empty') }}
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="file in bankFiles"
                  :key="file.id"
                  class="border-t border-gray-100">
                  <td
                    class="py-4 pr-3 text-gray-900 font-medium whitespace-nowrap">
                    {{ bankFileEntity(file.templateId) }}
                  </td>
                  <td class="py-4 px-3 whitespace-nowrap">
                    <div class="flex flex-col">
                      <span class="text-gray-900 font-medium">
                        {{ bankFileName(file) || '-' }}
                      </span>
                      <span class="text-xs text-gray-400">{{
                        file.format
                      }}</span>
                    </div>
                  </td>
                  <td class="py-4 px-3 min-w-[160px]">
                    <div class="flex items-center gap-2">
                      <span
                        class="flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                        :class="
                          file.status === 'generated'
                            ? 'bg-green-50'
                            : 'bg-red-50'
                        ">
                        <CheckIcon
                          v-if="file.status === 'generated'"
                          class="w-4 h-4 text-green-500" />
                        <AlertCircleIcon v-else class="w-4 h-4 text-red-500" />
                      </span>
                      <div>
                        <p
                          class="font-medium"
                          :class="
                            file.status === 'generated'
                              ? 'text-green-600'
                              : 'text-red-600'
                          ">
                          {{
                            t(`payrollSheet.bankFiles.status.${file.status}`)
                          }}
                        </p>
                        <p
                          v-if="file.error"
                          class="text-xs text-gray-400 max-w-xs truncate"
                          :title="file.error">
                          {{ file.error }}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td
                    class="py-4 px-3 text-right text-gray-700 whitespace-nowrap">
                    {{ file.totalTransactions }}
                  </td>
                  <td
                    class="py-4 px-3 text-right text-gray-700 whitespace-nowrap">
                    {{ formatBankFileAmount(file.totalAmount) }}
                  </td>
                  <td class="py-4 pl-3 text-right whitespace-nowrap">
                    <DBtn
                      v-if="file.status === 'generated'"
                      type="button"
                      :loading="downloadingBankFileId === file.id"
                      :disabled="downloadingBankFileId !== ''"
                      @click="downloadBankFile(file)">
                      <Download01Icon class="w-4 h-4 mr-2" />
                      {{ t('payrollSheet.bankFiles.download') }}
                    </DBtn>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </DCard>
    </form>
  </div>
</template>
